import React from 'react';
import { cn } from '../../../lib/utils';

interface ThemeBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
    variant?: 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'primary' | 'secondary';
    size?: 'sm' | 'md' | 'lg';
    children: React.ReactNode;
}

const ThemeBadge = React.forwardRef<HTMLDivElement, ThemeBadgeProps>(
    ({ className, variant = 'neutral', size = 'md', children, ...props }, ref) => {
        const baseClasses = "inline-flex items-center rounded-full font-medium transition-all shadow-sm";

        const variants = {
            neutral: "bg-gradient-to-br from-slate-100 to-slate-200 text-slate-700 border border-slate-300",
            info: "bg-gradient-to-br from-blue-100 to-blue-200 text-blue-700 border border-blue-300",
            success: "bg-gradient-to-br from-green-100 to-green-200 text-green-700 border border-green-300",
            warning: "bg-gradient-to-br from-yellow-100 to-yellow-200 text-yellow-700 border border-yellow-300",
            danger: "bg-gradient-to-br from-red-100 to-red-200 text-red-700 border border-red-300",
            primary: "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border border-[hsl(var(--primary))]",
            secondary: "bg-[hsl(var(--secondary))] text-[hsl(var(--secondary-foreground))] border border-[hsl(var(--secondary))]"
        };

        const sizes = {
            sm: "px-[calc(0.5rem*var(--density-scale))] py-[calc(0.125rem*var(--density-scale))] text-[calc(0.75rem*var(--density-scale))]",
            md: "px-[calc(0.75rem*var(--density-scale))] py-[calc(0.25rem*var(--density-scale))] text-[calc(0.875rem*var(--density-scale))]",
            lg: "px-[calc(1rem*var(--density-scale))] py-[calc(0.375rem*var(--density-scale))] text-[calc(1rem*var(--density-scale))]"
        };

        return (
            <div
                ref={ref}
                className={cn(baseClasses, variants[variant], sizes[size], className)}
                {...props}
            >
                {children}
            </div>
        );
    }
);

ThemeBadge.displayName = "ThemeBadge";

export { ThemeBadge };
